import * as React from 'react'
import { useNavigate, useFetcher, Link  } from 'react-router'
import { buttonVariants } from '#app/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
	CommandDialog,
} from '#app/components/ui/command'
import { useRootLoader } from '#app/utils/app'
import { useDebounce } from '#app/utils/misc'
import { getSearchType } from '#app/utils/search'
import { userHasPermission, type PermissionString } from '#app/utils/user'

// Define command items with their permissions
const commandItems = {
	students: [
		{
			label: 'Student Admission',
			path: '/students/admission',
			permission: 'create:student:any' as PermissionString
		},
		{
			label: 'New Admissions List',
			path: '/students/admissions',
			permission: 'read:student:any' as PermissionString
		},
		{
			label: 'Assign Classes for New Admissions',
			path: '/students/assign-class',
			permission: 'update:student:any' as PermissionString
		},
		{
			label: 'Promote students to next academic year',
			path: '/students/promotions',
			permission: 'update:student:any' as PermissionString
		},
	],
	accounting: [
		{
			label: 'Add Payment Entry',
			path: '/accounting/payment',
			permission: 'create:payment:any' as PermissionString
		},
		{
			label: 'Add Receipt Entry',
			path: '/accounting/receipt',
			permission: 'create:receipt:any' as PermissionString
		},
		{
			label: 'Create Ledger/Account Groups',
			path: '/accounting/ledger-groups',
			permission: 'create:ledger:any' as PermissionString
		},
		{
			label: 'Chart of Accounts',
			path: '/accounting/chart-of-accounts',
			permission: 'read:account:any' as PermissionString
		},
		{
			label: 'Payment & Receipt Settings',
			path: '/accounting/chart-of-accounts/payment-receipt-settings',
			permission: 'read:account:any' as PermissionString
		},
	],
	reports: [
		{
			label: 'Fee Reports',
			path: '/reports/fees',
			permission: 'read:feesReport:any' as PermissionString
		},
		{
			label: 'Financial Reports',
			path: '/reports/financial',
			permission: 'read:financesReport:any' as PermissionString
		}
	],
	fees: [
		{
			label: 'Add Fee Type',
			path: '/fees/types/add',
			permission: 'create:feeType:any' as PermissionString
		},
		{
			label: 'Add Fee Structure',
			path: '/settings/fees/fee-structures/create',
			permission: 'create:feeStructure:any' as PermissionString
		},
		{
			label: 'List Fee Structure',
			path: '/settings/fees/fee-structures',
			permission: 'read:feeStructure:any' as PermissionString
		},
		{
			label: 'List Fee Types',
			path: '/fees/types',
			permission: 'read:feeType:any' as PermissionString
		},
		{
			label: 'Fee Reports',
			path: '/reports/fees',
			permission: 'read:feesReport:any' as PermissionString
		},
	]
}

interface SearchResult {
	id: string
	name: string
	admissionNumber: string | null
	class: string
	type: string
	isNewAdmission?: boolean
}

interface SearchResponse {
	status: string
	results: SearchResult[]
}

export function AppCommand({
	defaultCommand,
	search,
	setSearch,
	dialogMode = false,
	open,
	onOpenChange,
}: {
	defaultCommand?: string
	search?: string
	setSearch?: (search: string) => void
	dialogMode?: boolean
	open?: boolean
	onOpenChange?: (open: boolean) => void
}) {
	const navigate = useNavigate()
	const fetcher = useFetcher<SearchResponse>({
		key: 'command-search'
	})

	const [searchValue, setSearchValue] = React.useState(defaultCommand || '')
	const controlledSearch = search ?? searchValue
	const controlledSetSearch = setSearch ?? setSearchValue

	// Get current user to check permissions
	const { user, isAdmin } = useRootLoader()

	// Get search results from fetcher
	const searchResults = fetcher.data?.results || []

	// Determine search type
	const searchType = getSearchType(controlledSearch)

	// Perform search when search type or query changes
	const debouncedSearch = useDebounce((searchTerm: string) => {
		const type = getSearchType(searchTerm)

		if (type) {
			// Extract the search query without the prefix
			const query = searchTerm.trim()
			if (query) {
				const searchUrl = `/search/${type}?q=${encodeURIComponent(searchTerm)}`;
				void fetcher.load(searchUrl);
			} 
		}
	}, 300)

	// Effect to trigger search
	React.useEffect(() => {
		if (controlledSearch) {
			void debouncedSearch(controlledSearch)
		}
	}, [controlledSearch, debouncedSearch])

	// Handle input change
	const handleInputChange = (value: string) => {
		controlledSetSearch(value)
	}

	// Determine what to display
	const isLoading = fetcher.state === 'loading'
	const showStudentResults = (searchType === 'student' || searchType === 'classStudent') && searchResults.length > 0
	const showDefaultCommands = !searchType || (searchType && searchResults.length === 0 && !isLoading)

	// Handle navigation
	const handleSelect = (path: string) => {
		void navigate(path)
		if (dialogMode && onOpenChange) {
			onOpenChange(false)
		}
	}

	// Filter command items based on user permissions
	const getFilteredCommandItems = (category: keyof typeof commandItems) => {
		return commandItems[category].filter(item => {
			// Allow all items for admin users
			if (isAdmin) return true
			// Otherwise check specific permission
			return userHasPermission(user, item.permission)
		})
	}

	// Check if any items exist in a category
	const hasAccessToCategory = (category: keyof typeof commandItems) => {
		return getFilteredCommandItems(category).length > 0
	}

	const commandContent = (
		<>
			<CommandInput
				placeholder="Type a command or search..."
				value={controlledSearch}
				onValueChange={handleInputChange}
			/>
			<CommandList>
				{!isLoading && !showStudentResults && <CommandEmpty>No results found.</CommandEmpty>}

				{isLoading && <div className="py-6 text-sm text-center">Searching...</div>}

				{/* Display student search results */}
				{showStudentResults && (
					<>
						{searchType === 'classStudent' && searchResults.length > 0 && (
							<div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
								{searchResults[0]?.class ? 
									`Students in Class ${searchResults[0].class}` : 
									'Students in Selected Class'
								}
							</div>
						)}
						{searchResults.map((student) => (
							<div key={student.id} className="flex items-center justify-between w-full p-2 border">
								<div className="flex items-center gap-4">
									<p className="font-medium">{student.name}</p>
									{student.class && (
										<div className="flex items-center gap-2">
											<p className="text-sm text-muted-foreground">
												{student.class.includes('-') ? `Class ${student.class}` : `Class ${student.class}`}
											</p>
											{student.isNewAdmission && (
												<span className="px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 font-medium">
													New Admission
												</span>
											)}
										</div>
									)}
									{student.admissionNumber && (
										<p className="text-xs text-muted-foreground">
											Admission: {student.admissionNumber}
										</p>
									)}
								</div>
								<div className="flex gap-2">
									<Link
										className={buttonVariants({
											variant: 'outline',
											size: 'sm'
										})}
										to={`/students/${student.id}`}
										onClick={() => dialogMode && onOpenChange && onOpenChange(false)}
									>
										View
									</Link>
									{(isAdmin || userHasPermission(user, 'create:payment:any' as PermissionString)) && (
										<Link
											className={buttonVariants({
												variant: 'default',
												size: 'sm'
											})}
											to={`/students/${student.id}/fee-payment`}
											onClick={() => dialogMode && onOpenChange && onOpenChange(false)}
										>
											Pay Fees
										</Link>
									)}
								</div>
							</div>
						))}
					</>
				)}

				{/* Display default command groups */}
				{showDefaultCommands && (
					<>
						{controlledSearch === '' && (
							<>
								<CommandGroup heading="Search">
									<div className="px-2 py-1.5 text-xs text-muted-foreground">
										<p className="mb-1">Try these search prefixes:</p>
										<ul className="pl-2 space-y-1">
											<li><code className="px-1 rounded bg-muted">s:</code> Search students by name or admission number (current academic year)</li>
											<li><code className="px-1 rounded bg-muted">cs:</code> Find students in a specific class (e.g. cs:1-A or cs:1-A mohammed)</li>
										</ul>
										<p className="mt-2 text-xs text-muted-foreground/80">
											Note: Search results are filtered to show only students from the current academic year.
										</p>
									</div>
								</CommandGroup>
								<CommandSeparator />
							</>
						)}

						{/* Only show categories if user has access to at least one item */}
						{hasAccessToCategory('students') && (
							<>
								<CommandGroup heading="Students">
									{getFilteredCommandItems('students').map(item => (
										<CommandItem key={item.path} onSelect={() => handleSelect(item.path)}>
											<span>{item.label}</span>
										</CommandItem>
									))}
								</CommandGroup>
								<CommandSeparator />
							</>
						)}

						{hasAccessToCategory('accounting') && (
							<>
								<CommandGroup heading="Accounting">
									{getFilteredCommandItems('accounting').map(item => (
										<CommandItem key={item.path} onSelect={() => handleSelect(item.path)}>
											<span>{item.label}</span>
										</CommandItem>
									))}
								</CommandGroup>
								<CommandSeparator />
							</>
						)}

						{hasAccessToCategory('reports') && (
							<>
								<CommandGroup heading="Reports">
									{getFilteredCommandItems('reports').map(item => (
										<CommandItem key={item.path} onSelect={() => handleSelect(item.path)}>
											<span>{item.label}</span>
										</CommandItem>
									))}
								</CommandGroup>
								<CommandSeparator />
							</>
						)}

						{hasAccessToCategory('fees') && (
							<>
								<CommandGroup heading="Fees">
									{getFilteredCommandItems('fees').map(item => (
										<CommandItem key={item.path} onSelect={() => handleSelect(item.path)}>
											<span>{item.label}</span>
										</CommandItem>
									))}
								</CommandGroup>
							</>
						)}
					</>
				)}
			</CommandList>
		</>
	)

	// Render as dialog or inline based on mode
	if (dialogMode) {
		return (
			<CommandDialog open={open} onOpenChange={onOpenChange}>
				{commandContent}
			</CommandDialog>
		)
	}

	return (
		<Command
			className="rounded-lg border shadow-md md:min-w-[450px]"
			filter={(value, search, keywords) => {
				const extendValue = value + ' ' + keywords?.join(' ')
				if (extendValue.toLowerCase().includes(search.toLowerCase())) return 1
				return 0
			}}
		>
			{commandContent}
		</Command>
	)
}
